import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getVolunteerDisplayName } from "./participantService";
import {
  buildVolunteerBarcodeImageUrl,
  buildVolunteerQrImageUrl,
  buildVolunteerQrPayload,
  formatParticipantDate,
  getVolunteerLabelId,
  getVolunteerStatusLabel,
} from "./participantReportUtils";
import "./ParticipantCodeLabelModal.scss";

function ParticipantCodeLabelModal({ row, onClose }) {
  const [mode, setMode] = useState("barcode");
  const [copied, setCopied] = useState(false);
  const printBusyRef = useRef(false);
  const printFrameRef = useRef(null);

  const name = getVolunteerDisplayName(row);
  const labelId = getVolunteerLabelId(row);
  const dobLabel = formatParticipantDate(row?.DOB);
  const statusLabel = getVolunteerStatusLabel(row?.VolStatus);
  const payload = useMemo(() => buildVolunteerQrPayload(row), [row]);
  const qrUrl = useMemo(() => buildVolunteerQrImageUrl(row, 280), [row]);
  const barcodeUrl = useMemo(() => buildVolunteerBarcodeImageUrl(row, 3), [row]);

  const cleanupPrintFrame = () => {
    const frame = printFrameRef.current;
    printFrameRef.current = null;
    printBusyRef.current = false;
    if (frame?.parentNode) {
      frame.parentNode.removeChild(frame);
    }
  };

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      cleanupPrintFrame();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- unmount cleanup for print iframe
  }, [onClose]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(payload);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      const area = document.createElement("textarea");
      area.value = payload;
      document.body.appendChild(area);
      area.select();
      document.execCommand("copy");
      document.body.removeChild(area);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    }
  };

  const handlePrint = () => {
    // Prevent double-clicks / repeated print commands.
    if (printBusyRef.current) return;
    const imageUrl = mode === "qr" ? qrUrl : barcodeUrl;
    if (!imageUrl) return;

    cleanupPrintFrame();
    printBusyRef.current = true;

    const modeLabel = mode === "qr" ? "QR code" : "Barcode";
    const esc = (value) =>
      String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

    // Print only the selected code (barcode or QR) + id under it.
    const html = `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${esc(labelId)} — ${esc(modeLabel)}</title>
  <style>
    @page { margin: 10mm; }
    html, body {
      margin: 0;
      padding: 0;
      height: 100%;
      font-family: "Segoe UI", system-ui, sans-serif;
      color: #0f172a;
    }
    body {
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .wrap { text-align: center; }
    img.barcode {
      display: block;
      max-width: 90mm;
      max-height: 28mm;
      margin: 0 auto;
    }
    img.qr {
      display: block;
      width: 55mm;
      height: 55mm;
      margin: 0 auto;
      object-fit: contain;
    }
    .id {
      margin-top: 8px;
      font-size: 14px;
      font-weight: 700;
      letter-spacing: 0.02em;
    }
  </style>
</head>
<body>
  <div class="wrap">
    <img id="label-img" class="${mode === "qr" ? "qr" : "barcode"}" src="${esc(imageUrl)}" alt="${esc(modeLabel)}" />
    <div class="id">${esc(labelId)}</div>
  </div>
</body>
</html>`;

    // Hidden iframe only — never window.open (avoids leftover browser tabs).
    const iframe = document.createElement("iframe");
    iframe.setAttribute("title", "Print participant code");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.cssText =
      "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;";
    document.body.appendChild(iframe);
    printFrameRef.current = iframe;

    const frameWindow = iframe.contentWindow;
    const frameDoc = frameWindow?.document;
    if (!frameDoc || !frameWindow) {
      cleanupPrintFrame();
      return;
    }

    frameDoc.open();
    frameDoc.write(html);
    frameDoc.close();

    let printed = false;
    const runPrintOnce = () => {
      if (printed) return;
      printed = true;
      try {
        frameWindow.focus();
        frameWindow.print();
      } catch {
        // ignore
      } finally {
        // Release after dialog closes (or shortly if afterprint is unsupported).
        const release = () => cleanupPrintFrame();
        frameWindow.addEventListener("afterprint", release, { once: true });
        window.setTimeout(release, 2000);
      }
    };

    const img = frameDoc.getElementById("label-img");
    if (img && !img.complete) {
      img.addEventListener("load", runPrintOnce, { once: true });
      img.addEventListener("error", runPrintOnce, { once: true });
      window.setTimeout(runPrintOnce, 2000);
    } else {
      window.setTimeout(runPrintOnce, 50);
    }
  };

  if (!row) return null;

  return createPortal(
    <div
      className="pm-code-label-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pm-code-label-title"
      onClick={onClose}
    >
      <div
        className="pm-code-label-modal__dialog"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="pm-code-label-modal__header">
          <div className="pm-code-label-modal__titles">
            <h2 id="pm-code-label-title">{name}</h2>
            <p>
              {labelId} · {mode === "qr" ? "qr code label" : "barcode label"}
            </p>
          </div>
          <div className="pm-code-label-modal__header-actions">
            <div className="pm-code-label-modal__toggle" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={mode === "barcode"}
                className={mode === "barcode" ? "is-active" : ""}
                onClick={() => setMode("barcode")}
              >
                Barcode
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={mode === "qr"}
                className={mode === "qr" ? "is-active" : ""}
                onClick={() => setMode("qr")}
              >
                QR code
              </button>
            </div>
            <button
              type="button"
              className="pm-code-label-modal__close"
              aria-label="Close"
              onClick={onClose}
            >
              <i className="fas fa-times" aria-hidden="true" />
            </button>
          </div>
        </header>

        <div className="pm-code-label-modal__body">
          <div className="pm-code-label-modal__code-pane">
            <div
              className={[
                "pm-code-label-modal__code-card",
                mode === "qr" ? "is-qr" : "is-barcode",
              ].join(" ")}
            >
              {mode === "qr" ? (
                <img src={qrUrl} alt={`QR code for ${name}`} />
              ) : barcodeUrl ? (
                <img src={barcodeUrl} alt={`Barcode for ${name}`} />
              ) : (
                <span className="text-muted">No barcode available</span>
              )}
            </div>
            <div className="pm-code-label-modal__code-id">{labelId}</div>
          </div>

          <div className="pm-code-label-modal__details">
            <h3>Encoded details</h3>
            <dl>
              <div>
                <dt>Participant ID</dt>
                <dd>{labelId}</dd>
              </div>
              <div>
                <dt>Name</dt>
                <dd title={name}>{name}</dd>
              </div>
              <div>
                <dt>Date of birth</dt>
                <dd>{dobLabel}</dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd>{statusLabel}</dd>
              </div>
            </dl>
          </div>
        </div>

        <footer className="pm-code-label-modal__footer">
          <p>Scan at check-in to open this participant&apos;s visit.</p>
          <div className="pm-code-label-modal__footer-actions">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={handleCopy}
            >
              <i className="far fa-copy me-1" aria-hidden="true" />
              {copied ? "Copied" : "Copy payload"}
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                handlePrint();
              }}
            >
              <i className="fas fa-print me-1" aria-hidden="true" />
              Print label
            </button>
          </div>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

export default ParticipantCodeLabelModal;
